import { useState, type ReactNode } from "react"
import { useQuery } from "@tanstack/react-query"
import { Bell, Bot, Building2, Loader2, PlugZap, Save, ShieldCheck } from "lucide-react"

import { Page, PageHeader, Panel, Tag, type Tone } from "@/components/superadmin/kit"
import { toast } from "sonner"

import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Switch } from "@/components/ui/switch"
import { modeloIAPadrao, salvarModeloIAPadrao, useModelosIA, verificarIA, type StatusIA } from "@/lib/ai"
import { cn } from "@/lib/utils"

type SettingsSection = "platform" | "ai" | "notifications" | "security"
type NotificationKey = "organization" | "overdue" | "license" | "proposal" | "digest"
type NotificationSettings = Record<NotificationKey, boolean>

const sections: Array<{ value: SettingsSection; label: string }> = [
  { value: "platform", label: "Plataforma" },
  { value: "ai", label: "Integrações e IA" },
  { value: "notifications", label: "Notificações" },
  { value: "security", label: "Segurança" },
]

const SECTION_ICONS: Record<SettingsSection, typeof Bell> = { platform: Building2, ai: Bot, notifications: Bell, security: ShieldCheck }

const defaultNotifications: NotificationSettings = {
  organization: true,
  overdue: true,
  license: true,
  proposal: true,
  digest: false,
}

const readNotifications = (): NotificationSettings => {
  try {
    const saved = localStorage.getItem("superadmin-notifications")
    return saved ? { ...defaultNotifications, ...JSON.parse(saved) } : defaultNotifications
  } catch {
    return defaultNotifications
  }
}

const AI_STATUS: Record<StatusIA["estado"], { label: string; tone: Tone }> = {
  ok: { label: "IA conectada", tone: "success" },
  sem_chave: { label: "Sem chave", tone: "warning" },
  nao_implantada: { label: "Função não publicada", tone: "danger" },
  erro: { label: "Com erro", tone: "danger" },
}

const Row = ({ label, hint, htmlFor, children }: { label: string; hint?: string; htmlFor?: string; children: ReactNode }) => (
  <div className="grid gap-2 border-t border-border px-4 py-4 first:border-t-0 md:grid-cols-[220px_minmax(0,1fr)] md:gap-6">
    <div>
      <Label htmlFor={htmlFor} className="text-[13px] font-medium text-foreground">{label}</Label>
      {hint && <p className="mt-0.5 text-[12px] text-muted-foreground">{hint}</p>}
    </div>
    <div className="min-w-0 max-w-md">{children}</div>
  </div>
)

const SuperAdminSettings = () => {
  const [activeSection, setActiveSection] = useState<SettingsSection>("platform")
  const [platform, setPlatform] = useState(() => ({
    name: localStorage.getItem("itacondo-platform-name") || "itaCondo",
    domain: localStorage.getItem("itacondo-platform-domain") || "app.itacondo.com.br",
    support: localStorage.getItem("itacondo-platform-support") || "suporte@itacondo.com.br",
    timezone: localStorage.getItem("itacondo-platform-timezone") || "America/Sao_Paulo",
  }))
  const [model, setModel] = useState(modeloIAPadrao)
  const { modelos: aiModels, carregando: aiModelsLoading } = useModelosIA(model)
  const aiStatus = useQuery({
    queryKey: ["superadmin-ai-status"],
    queryFn: () => verificarIA(),
    enabled: activeSection === "ai",
    staleTime: 60_000,
    retry: false,
  })
  const [aiTest, setAiTest] = useState<StatusIA | null>(null)
  const [aiTesting, setAiTesting] = useState(false)
  const testAi = async () => {
    setAiTesting(true)
    setAiTest(null)
    try {
      const result = await verificarIA(true, model)
      setAiTest(result)
      if (result.estado === "ok") toast.success("IA respondeu.")
      else toast.error(result.mensagem)
    } finally {
      setAiTesting(false)
    }
  }
  const [notifications, setNotifications] = useState<NotificationSettings>(readNotifications)
  const [sessionHours, setSessionHours] = useState(() => localStorage.getItem("superadmin-session-hours") || "8")
  const [require2fa, setRequire2fa] = useState(() => localStorage.getItem("superadmin-require-2fa") === "true")
  const [auditLog, setAuditLog] = useState(() => localStorage.getItem("superadmin-audit-log") !== "false")

  const savePlatform = () => {
    Object.entries(platform).forEach(([key, value]) => localStorage.setItem(`itacondo-platform-${key}`, value))
    toast.success("Identidade da plataforma salva.")
  }

  const saveAi = () => {
    salvarModeloIAPadrao(model)
    toast.success("Modelo padrão da IA salvo.")
  }

  const saveNotifications = () => {
    localStorage.setItem("superadmin-notifications", JSON.stringify(notifications))
    toast.success("Preferências de notificação salvas.")
  }

  const saveSecurity = () => {
    localStorage.setItem("superadmin-session-hours", sessionHours)
    localStorage.setItem("superadmin-require-2fa", String(require2fa))
    localStorage.setItem("superadmin-audit-log", String(auditLog))
    toast.success("Preferências de segurança salvas.")
  }

  const footer = (onSave: () => void, extra?: ReactNode) => (
    <div className="flex justify-end gap-2 border-t border-border bg-[hsl(228_8%_7.5%)] px-4 py-3">
      {extra}
      <Button size="sm" className="h-8 gap-1.5" onClick={onSave}><Save className="h-4 w-4" />Salvar</Button>
    </div>
  )

  return (
    <Page>
      <PageHeader title="Configurações" description="Identidade da plataforma, integrações, notificações e segurança do superadmin." />

      <div className="lg:hidden">
        <Select value={activeSection} onValueChange={(value) => setActiveSection(value as SettingsSection)}>
          <SelectTrigger aria-label="Seção das configurações" className="h-9"><SelectValue /></SelectTrigger>
          <SelectContent>{sections.map((section) => <SelectItem key={section.value} value={section.value}>{section.label}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      <div className="grid min-w-0 gap-6 lg:grid-cols-[200px_minmax(0,1fr)]">
        <nav className="hidden flex-col gap-0.5 lg:flex" aria-label="Seções das configurações">
          {sections.map((section) => {
            const Icon = SECTION_ICONS[section.value]
            return (
              <button
                key={section.value}
                type="button"
                onClick={() => setActiveSection(section.value)}
                className={cn(
                  "flex h-8 items-center gap-2.5 rounded-md px-2.5 text-left text-[13px] font-medium text-muted-foreground transition-colors hover:bg-[hsl(228_8%_11%)] hover:text-foreground",
                  activeSection === section.value && "bg-[hsl(228_8%_12.5%)] text-foreground",
                )}
              >
                <Icon className="h-4 w-4" />{section.label}
              </button>
            )
          })}
        </nav>

        <div className="min-w-0">
          {activeSection === "platform" && (
            <Panel title="Identidade da plataforma" description="Como a plataforma se apresenta em e-mails e páginas públicas." flush>
              {([["Nome exibido", "name"], ["Domínio", "domain"], ["E-mail de suporte", "support"]] as const).map(([label, key]) => (
                <Row key={key} label={label} htmlFor={`platform-${key}`}>
                  <Input id={`platform-${key}`} className="h-9" value={platform[key]} onChange={(event) => setPlatform((current) => ({ ...current, [key]: event.target.value }))} />
                </Row>
              ))}
              <Row label="Fuso horário" hint="Usado em datas de propostas e relatórios.">
                <Select value={platform.timezone} onValueChange={(timezone) => setPlatform((current) => ({ ...current, timezone }))}>
                  <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="America/Sao_Paulo">América/São Paulo (GMT-3)</SelectItem><SelectItem value="America/Manaus">América/Manaus (GMT-4)</SelectItem></SelectContent>
                </Select>
              </Row>
              {footer(savePlatform, <Button size="sm" variant="ghost" className="h-8" onClick={() => setPlatform({ name: "itaCondo", domain: "app.itacondo.com.br", support: "suporte@itacondo.com.br", timezone: "America/Sao_Paulo" })}>Restaurar padrão</Button>)}
            </Panel>
          )}

          {activeSection === "ai" && (
            <Panel
              title="Integrações e IA"
              description="Geração de textos de propostas e atas."
              actions={aiStatus.isFetching
                ? <Tag tone="neutral"><Loader2 className="h-3 w-3 animate-spin" />Verificando</Tag>
                : aiStatus.data && <Tag tone={AI_STATUS[aiStatus.data.estado].tone} dot>{aiStatus.data.estado === "ok" ? `${aiStatus.data.provedor} conectada` : AI_STATUS[aiStatus.data.estado].label}</Tag>}
              flush
            >
              <Row label="Provedor e chave" hint="Ficam em secrets da Edge Function ai-generate (AI_BASE_URL, AI_API_KEY, AI_DEFAULT_MODEL), nunca no navegador. Trocar de provedor é só trocar os secrets.">
                <div className="space-y-2 text-[13px]">
                  <p className={aiStatus.data?.estado === "ok" ? "text-foreground" : "text-muted-foreground"}>
                    {aiStatus.isFetching ? "Consultando o servidor…" : aiStatus.data?.mensagem ?? (aiStatus.error ? String(aiStatus.error) : "—")}
                  </p>
                  {aiStatus.data && aiStatus.data.estado !== "ok" && (
                    <div className="rounded-lg border border-border bg-[hsl(228_8%_7.5%)] p-3 text-[12px] text-muted-foreground">
                      <p className="mb-1.5">Para ativar, rode no terminal do projeto:</p>
                      <pre className="overflow-x-auto font-mono text-[11px] leading-relaxed text-foreground">
                        {aiStatus.data.estado === "nao_implantada" && "npx supabase functions deploy ai-generate\n"}
                        {"npx supabase secrets set GROQ_API_KEY=gsk_..."}
                      </pre>
                    </div>
                  )}
                </div>
              </Row>
              <Row label="Testar conexão" hint="Faz uma geração curta de verdade com o modelo selecionado.">
                <div className="flex flex-wrap items-center gap-3">
                  <Button size="sm" variant="outline" className="h-8 gap-1.5" onClick={testAi} disabled={aiTesting}>
                    {aiTesting ? <Loader2 className="h-4 w-4 animate-spin" /> : <PlugZap className="h-4 w-4" />}
                    {aiTesting ? "Testando…" : "Testar"}
                  </Button>
                  {aiTest && (
                    <span className={cn("text-[12px]", aiTest.estado === "ok" ? "text-[hsl(152_60%_66%)]" : "text-[hsl(358_90%_76%)]")}>
                      {aiTest.mensagem}{aiTest.estado === "ok" && aiTest.ms ? ` · ${aiTest.ms} ms` : ""}
                    </span>
                  )}
                </div>
              </Row>
              <Row label="Modelo padrão" hint={aiModelsLoading ? "Carregando modelos do provedor…" : "Lista lida da conta do provedor. Se o modelo sair do ar, a função usa o padrão."}>
                <Select value={model} onValueChange={setModel}>
                  <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                  <SelectContent>{aiModels.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}</SelectContent>
                </Select>
              </Row>
              {footer(saveAi)}
            </Panel>
          )}

          {activeSection === "notifications" && (
            <Panel title="Notificações" description="Avisos que o superadmin recebe." flush>
              {([
                ["organization", "Nova organização cadastrada", "Quando um condomínio ou empresa entra na plataforma."],
                ["overdue", "Pagamento em atraso", "Assinatura passou do vencimento."],
                ["license", "Licença vencendo", "30 dias antes do fim do prazo."],
                ["proposal", "Proposta aceita", "Cliente aceitou pelo link público."],
                ["digest", "Resumo semanal por e-mail", "Toda segunda-feira, com os números da semana."],
              ] as const).map(([key, label, hint]) => (
                <Row key={key} label={label} hint={hint} htmlFor={`notification-${key}`}>
                  <Switch id={`notification-${key}`} checked={notifications[key]} onCheckedChange={(checked) => setNotifications((current) => ({ ...current, [key]: checked }))} />
                </Row>
              ))}
              {footer(saveNotifications)}
            </Panel>
          )}

          {activeSection === "security" && (
            <Panel title="Segurança" description="Contas Super Admin têm acesso total. Revise periodicamente." flush>
              <Row label="Expiração de sessão" hint="Tempo até pedir login de novo.">
                <Select value={sessionHours} onValueChange={setSessionHours}>
                  <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                  <SelectContent>{["2", "4", "8", "24"].map((hours) => <SelectItem key={hours} value={hours}>{hours} horas</SelectItem>)}</SelectContent>
                </Select>
              </Row>
              <Row label="Exigir 2FA" hint="Para todas as contas Super Admin." htmlFor="require-2fa">
                <Switch id="require-2fa" checked={require2fa} onCheckedChange={setRequire2fa} />
              </Row>
              <Row label="Log de auditoria" hint="Registra ações administrativas." htmlFor="audit-log">
                <Switch id="audit-log" checked={auditLog} onCheckedChange={setAuditLog} />
              </Row>
              {footer(saveSecurity)}
            </Panel>
          )}
        </div>
      </div>
    </Page>
  )
}

export { SuperAdminSettings }
